import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import {
  CheckboxLine,
  EmptyState,
  FilterSelect,
  HpButton,
  HpCardHeader,
  SearchField,
  Segmented,
  TableFooter,
  Toolbar,
  ToolbarSpacer,
} from '@hp/ui';
import { useCan } from '../../api/me';
import { ErrorAlert } from '../../components/error-alert';
import { Spinner } from '../../components/ui/icons';
import { fmtInt } from '../../lib/format';
import { marketFrom, SCOPES } from './labels';
import { useCatalog, useMarketDefaults, useOwners, type CatalogScope, type MarketCode } from './queries';
import { RuleEditor, type EditorTarget } from './rule-editor';
import { RulesTable } from './rules-table';

const LIMIT = 300;
/** The catalog's maximum page: a `?campaign=` lookup reads every campaign once. */
const LOOKUP_LIMIT = 500;

/** Tab "Assignment rules": the ad campaign catalog with its rules, and the rule editor above it. */
export function RulesTab() {
  const { t } = useTranslation(['leads', 'common']);
  const navigate = useNavigate();
  const canManage = useCan()('leads.manage');
  const [scope, setScope] = useState<CatalogScope>('unruled');
  const [market, setMarket] = useState<MarketCode | null>(null);
  const [onlyRunning, setOnlyRunning] = useState(true);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<EditorTarget | null>(null);
  const [closedStale, setClosedStale] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  const [params, setParams] = useSearchParams();
  // `?campaign=<adCampaignId>` (from a received lead's detail): open that campaign's rule, or a new rule for it.
  const campaignParam = canManage ? params.get('campaign') : null;
  const [handledCampaign, setHandledCampaign] = useState<string | null>(null);
  const [campaignMissing, setCampaignMissing] = useState(false);
  const lookup = useCatalog(
    { scope: 'all', onlyRunning: false, limit: LOOKUP_LIMIT },
    campaignParam !== null && campaignParam !== handledCampaign,
  );

  const catalog = useCatalog({
    scope,
    market: market ?? undefined,
    onlyRunning,
    q: q || undefined,
    limit: LIMIT,
  });
  // The markets and their names come from the API (the market defaults: one per market).
  const markets = (useMarketDefaults().data ?? []).flatMap((d) =>
    d.marketCode ? [{ code: d.marketCode, label: d.marketLabel ?? d.marketCode }] : [],
  );
  const owners = useOwners();
  const activeIds = owners.data ? new Set(owners.data.map((o) => o.pdId)) : null;
  const rows = catalog.data?.rows ?? [];

  const openEditor = (target: EditorTarget) => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setClosedStale(false);
    setCampaignMissing(false);
    setEditing(target);
  };
  const closeEditor = () => {
    setEditing(null);
    // Back to the button that opened the panel, when it is still on the page.
    const back = opener.current;
    requestAnimationFrame(() => {
      if (back?.isConnected) back.focus();
    });
  };

  // The editor holds a snapshot: when its campaign's rule changes under it (paused, removed, created or
  // edited elsewhere), close it rather than let Save write stale values. Rows hidden by a filter are not a change.
  const shownRow = editing?.row ? rows.find((r) => r.campaign.id === editing.row?.campaign.id) : undefined;
  const editedRule = editing?.row?.rule ?? null;
  const stale =
    shownRow !== undefined &&
    (editedRule === null
      ? shownRow.rule !== null
      : shownRow.rule?.id !== editedRule.id || shownRow.rule.version !== editedRule.version);
  if (stale) {
    // Adjusting state while rendering (React's documented pattern): the stale panel never paints.
    setEditing(null);
    setClosedStale(true);
  }
  if (campaignParam !== null && campaignParam !== handledCampaign && lookup.data) {
    // Adjusting state while rendering, once per param value: open the editor before the first paint.
    const target = lookup.data.rows.find((r) => String(r.campaign.id) === campaignParam);
    setHandledCampaign(campaignParam);
    setClosedStale(false);
    setCampaignMissing(target === undefined);
    if (target) setEditing({ kind: target.rule ? 'edit' : 'new', row: target });
  }
  const counts = catalog.data?.counts;
  const total = counts ? counts[scope] : 0;
  // Unfiltered probe: the empty state is for "no campaigns imported at all", not for filters that match nothing.
  const everything = useCatalog({ limit: 1 });
  const nothingAtAll = everything.data?.counts.all === 0;

  // Once the editor is open (or the campaign was not found), the param has done its job: drop it from the URL.
  useEffect(() => {
    if (handledCampaign === null || params.get('campaign') !== handledCampaign) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('campaign');
        return next;
      },
      { replace: true },
    );
  }, [handledCampaign, params, setParams]);

  if (nothingAtAll) {
    return (
      <section className="hp-card">
        <EmptyState
          icon="megaphone"
          title={t('rules.emptyCatalog.title')}
          action={
            <HpButton variant="secondary" icon="database" onClick={() => void navigate('/marketing/performance/data')}>
              {t('rules.emptyCatalog.link')}
            </HpButton>
          }
        >
          {t('rules.emptyCatalog.body')}
        </EmptyState>
      </section>
    );
  }

  return (
    <>
      {editing && (
        <RuleEditor
          key={editing.row ? `${editing.kind}-${editing.row.campaign.id}` : 'new'}
          target={editing}
          onClose={closeEditor}
        />
      )}
      {campaignMissing && (
        <p className="bo-notice" role="status">
          {t('rules.campaignNotFound')}
        </p>
      )}
      {closedStale && (
        <p className="bo-notice" role="status">
          {t('rules.editorClosedStale')}
        </p>
      )}
      <section className="hp-card">
        <HpCardHeader title={t('rules.title')} subtitle={t('rules.subtitle')} icon="team">
          {canManage && (
            <HpButton
              variant="secondary"
              icon="plus"
              className="bo-button--sm"
              onClick={() => openEditor({ kind: 'new', row: null })}
            >
              {t('rules.newRule')}
            </HpButton>
          )}
        </HpCardHeader>
        <Toolbar className="bo-toolbar">
          <Segmented
            ariaLabel={t('rules.scopeAria')}
            value={scope}
            options={SCOPES.map((s) => ({
              value: s,
              label: counts ? t(`rules.scope.${s}`, { count: counts[s] }) : t(`rules.scopeBare.${s}`),
            }))}
            onChange={setScope}
          />
          <FilterSelect
            value={market ?? ''}
            aria-label={t('rules.marketAria')}
            onChange={(e) => setMarket(marketFrom(e.target.value, markets))}
          >
            <option value="">{t('rules.allMarkets')}</option>
            {markets.map((m) => (
              <option key={m.code} value={m.code}>
                {m.label}
              </option>
            ))}
          </FilterSelect>
          <CheckboxLine
            checked={onlyRunning}
            title={t('rules.onlyRunningTitle')}
            onChange={(e) => setOnlyRunning(e.target.checked)}
          >
            {t('rules.onlyRunning')}
          </CheckboxLine>
          <ToolbarSpacer />
          <SearchField
            value={q}
            placeholder={t('rules.search')}
            aria-label={t('rules.search')}
            onChange={(e) => setQ(e.target.value)}
          />
        </Toolbar>
        <ErrorAlert error={catalog.error} className="bo-alert--inset" />
        {catalog.isLoading ? (
          <p className="hp-card__body">
            <Spinner>{t('common:actions.loading')}</Spinner>
          </p>
        ) : (
          <RulesTable
            rows={rows}
            canManage={canManage}
            activeIds={activeIds}
            onEdit={(row) => openEditor({ kind: row.rule ? 'edit' : 'new', row })}
          />
        )}
        {catalog.data && (
          <TableFooter>{t('rules.showing', { shown: fmtInt(rows.length), total: fmtInt(total) })}</TableFooter>
        )}
        <div className="hp-card__body bo-sections">
          <p className="bo-hint">{t('rules.hint')}</p>
          <ul className="bo-hint">
            <li>{t('rules.legend.onlyRunning')}</li>
            <li>{t('rules.legend.market')}</li>
            <li>{t('rules.legend.ownerInactive')}</li>
            <li>{t('rules.legend.existingOwner')}</li>
            <li>{t('rules.legend.byDefault')}</li>
          </ul>
        </div>
      </section>
    </>
  );
}
